import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Actions } from '~/eds/src/components/Actions/Actions';
import { Card } from '~/eds/src/components/Card/Card';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Text } from '~/eds/src/components/Text/Text';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { User } from '~/eds/src/components/User/User';
import { Nullable } from '~/eds/src/types/Nullable';
import { User as EdsUser } from '~/eds/src/types/User';
import { UserAction } from '~/eds/src/types/UserAction';
import { getUserName } from '~/eds/src/utils/getUserName';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';

import { AiMessage } from '../../chat/AiMessage';
import { MESSAGE_ACTIONS_CLASSNAME } from '../../chat/constants';
import { ThinkingEvents } from '../../chat/ThinkingEvents';
import { FeedbackDetails, Message as MessageType } from '../../types/chat';
import { WorkflowCard } from './WorkflowCard';
import { WorkflowMessageActions } from './WorkflowMessageActions';
import { WorkflowMessageHeader } from './WorkflowMessageHeader';
import type { WorkflowSuggestion } from './WorkflowRoutingAgent';

const WORKFLOW_ROUTING_MESSAGE_ID = 'workflow-routing-message';

interface ExtendedMessageType extends MessageType {
  workflowChoices?: WorkflowSuggestion[];
  thinkingEvents?: string[];
  messageText?: string;
}

interface MessageRendererProps {
  message: MessageType;
  index: number;
  defaultProps: {
    disableChoices: boolean;
    messageActions: UserAction[];
    user: EdsUser;
    userMessageActions?: UserAction[];
    onChoose: (
      message: MessageType,
      choice: string | WorkflowSuggestion,
    ) => void;
    onSelectSource: (message: MessageType, sourceIndex: number) => void;
    onSubmitFeedback: (
      message: MessageType,
      feedbackDetails: FeedbackDetails,
    ) => void;
    shouldDisableSources?: (message: MessageType) => boolean;
    shouldShowActions: boolean;
    thinkingEvents?: string[];
    onWorkflowClick: (workflow: WorkflowSuggestion) => void;
  };
}

const workflowMessageStyles = {
  message: {
    position: 'relative' as const,
    [`&:hover .${MESSAGE_ACTIONS_CLASSNAME}`]: {
      visibility: 'visible',
    },
    [`&.current:hover + .other .${MESSAGE_ACTIONS_CLASSNAME}`]: {
      visibility: 'visible',
    },
  },
};

const cardStyles = {
  wrapper: {
    minHeight: '56px',
    '& > div': {
      minHeight: '56px',
    },
  },
};

const getThinkingEvents = (
  message: MessageType,
  defaultThinkingEvents?: string[],
): string[] | undefined => {
  const extendedMessage = message as ExtendedMessageType;
  return extendedMessage.thinkingEvents || defaultThinkingEvents;
};

const getWorkflowChoices = (
  message: MessageType,
): WorkflowSuggestion[] | undefined => {
  const extendedMessage = message as ExtendedMessageType;
  return extendedMessage.workflowChoices;
};

const getMessageText = (message: MessageType): string | undefined => {
  const extendedMessage = message as ExtendedMessageType;
  return extendedMessage.messageText;
};

const isWorkflowRoutingMessage = (
  message: MessageType,
  workflowChoices?: WorkflowSuggestion[],
  thinkingEvents?: string[],
  messageText?: string,
): boolean => {
  return (
    Boolean(workflowChoices?.length) ||
    Boolean(thinkingEvents?.length) ||
    Boolean(messageText) ||
    message.id === WORKFLOW_ROUTING_MESSAGE_ID
  );
};

export const WorkflowRoutingMessageRenderer = ({
  message,
  defaultProps,
}: MessageRendererProps) => {
  const {
    disableChoices,
    messageActions,
    user,
    userMessageActions,
    onChoose,
    onSelectSource,
    onSubmitFeedback,
    shouldShowActions,
    thinkingEvents: defaultThinkingEvents,
    onWorkflowClick,
  } = defaultProps;

  const intl = useIntl();
  const formatDate = useFormatDate();

  const thinkingEvents = useMemo(
    () => getThinkingEvents(message, defaultThinkingEvents),
    [message, defaultThinkingEvents],
  );
  const workflowChoices = useMemo(() => getWorkflowChoices(message), [message]);
  const messageText = useMemo(() => getMessageText(message), [message]);

  const hasChoices = Boolean(message.choices?.length);
  const hasWorkflows = Boolean(workflowChoices?.length);
  const hasThinkingEvents = Boolean(thinkingEvents?.length);
  const hasMessageText = Boolean(messageText);
  const hasCollapsibleContent = hasThinkingEvents || hasMessageText;
  const isWorkflowMessage = isWorkflowRoutingMessage(
    message,
    workflowChoices,
    thinkingEvents,
    messageText,
  );

  const [isCollapsed, setIsCollapsed] = useState(false);
  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const handleSubmitFeedback = useCallback(
    (value: Nullable<boolean>, comment?: string) => {
      onSubmitFeedback(message, { value, comment });
    },
    [message, onSubmitFeedback],
  );

  const tooltip = useMemo(
    () =>
      `${getUserName({ user, intl })}\n${formatDate(message.timestamp, 'short_datetime')}`,
    [user, intl, formatDate, message.timestamp],
  );

  if (message.userType === 'current') {
    return (
      <Layout direction="column" px={6} py={2} mx={1} spacing={4}>
        <Layout justify="space-between" align="center">
          <Layout align="center" spacing={4}>
            <Layout alignSelf="flex-start">
              <Tooltip tooltip={tooltip} placement="left">
                <User
                  mode="avatar"
                  user={user}
                  options={{ disableTooltip: true }}
                />
              </Tooltip>
            </Layout>
            <Markdown variant="body-medium" text={message.text} />
          </Layout>

          <Layout alignSelf="flex-start">
            {userMessageActions && <Actions actions={userMessageActions} />}
          </Layout>
        </Layout>
      </Layout>
    );
  }

  if (isWorkflowMessage && !disableChoices) {
    return (
      <Layout
        direction="column"
        px={6}
        py={2}
        mx={1}
        spacing={4}
        className="other"
        styles={workflowMessageStyles.message}
      >
        <Layout direction="column" spacing={4}>
          <WorkflowMessageHeader
            messageText={message.text}
            tooltip={tooltip}
            showChevron={hasCollapsibleContent}
            isCollapsed={isCollapsed}
            onToggleCollapsed={toggleCollapsed}
          />
          {!isCollapsed && hasThinkingEvents && (
            <Layout direction="column" spacing={3} w="100%" pl="13px">
              <Layout mb={2}>
                <ThinkingEvents events={thinkingEvents!} />
              </Layout>
            </Layout>
          )}
          {!isCollapsed && hasMessageText && (
            <Layout direction="column" mx={8} spacing={6} w="90%">
              <Layout mb={2}>
                <Markdown variant="body" text={messageText!} />
              </Layout>
              {hasWorkflows && (
                <Layout
                  direction="column"
                  spacing={3}
                  role="list"
                  aria-label={intl.formatMessage({
                    id: 'WorkflowRoutingMessageRenderer.suggestedWorkflows',
                    defaultMessage: 'Suggested workflows',
                    description:
                      'Accessibility label for suggested workflows list',
                  })}
                >
                  {workflowChoices?.map((workflow, index) => (
                    <WorkflowCard
                      key={`workflow-${index}`}
                      workflow={workflow}
                      index={index}
                      message={message}
                      onWorkflowClick={onWorkflowClick}
                    />
                  ))}
                </Layout>
              )}
            </Layout>
          )}
        </Layout>

        <WorkflowMessageActions
          shouldShowActions={shouldShowActions}
          messageActions={messageActions}
          message={message}
          onSubmitFeedback={handleSubmitFeedback}
        />
      </Layout>
    );
  }

  if (hasChoices && !disableChoices) {
    return (
      <Layout direction="column" spacing={3}>
        <Layout direction="column" px={6} py={2} mx={1} spacing={4}>
          <AiMessage
            message={{ ...message, choices: [] }}
            user={user}
            messageActions={messageActions}
            disableChoices={disableChoices}
            onChoose={onChoose}
            onSelectSource={onSelectSource}
            onSubmitFeedback={onSubmitFeedback}
            shouldShowActions={shouldShowActions}
          />
        </Layout>

        <Layout
          direction="column"
          spacing={4}
          px={6}
          mb={4}
          w="100%"
          role="list"
          aria-label={intl.formatMessage({
            id: 'WorkflowRoutingMessageRenderer.responseOptions',
            defaultMessage: 'Response options',
            description: 'Accessibility label for response options list',
          })}
        >
          {message.choices?.map((choice, choiceIndex) => (
            <Layout
              key={choiceIndex}
              role="listitem"
              w="100%"
              styles={cardStyles.wrapper}
            >
              <Card
                role="button"
                mode="bordered"
                size="m"
                width="100%"
                onClick={() => onChoose(message, choice)}
                tabIndex={0}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChoose(message, choice);
                  }
                }}
              >
                <Layout
                  direction="row"
                  align="center"
                  spacing={3}
                  py={2}
                  w="100%"
                >
                  <Icon icon="evisort-ai" size="s" />
                  <Text variant="body" flex={1}>
                    {choice}
                  </Text>
                </Layout>
              </Card>
            </Layout>
          ))}
        </Layout>
      </Layout>
    );
  }

  return (
    <Layout direction="column" px={6} py={2} mx={1} spacing={4}>
      <AiMessage
        message={message}
        user={user}
        messageActions={messageActions}
        disableChoices={disableChoices}
        onChoose={onChoose}
        onSelectSource={onSelectSource}
        onSubmitFeedback={onSubmitFeedback}
        shouldShowActions={shouldShowActions}
      />
    </Layout>
  );
};
